import React, { useMemo } from 'react';
import type { EvaluationPlanRowScope, EvaluationStandardDraft } from '../../types/evaluationPlan';
import {
  compareStandardsByCode,
  formatStandardSummary,
  standardMatchesFilter,
} from '../../types/evaluationPlan';
import EvaluationPlanDeleteHoldButton from './EvaluationPlanDeleteHoldButton';

const EvaluationStandardListPanel: React.FC<{
  standards: EvaluationStandardDraft[];
  selectedStandardId: string | null;
  filterQuery: string;
  readOnly?: boolean;
  allowAddStandard?: boolean;
  allowBulkEdit?: boolean;
  editableStandardIds?: Set<string>;
  standardScopeById?: Record<string, EvaluationPlanRowScope>;
  onFilterQueryChange: (value: string) => void;
  onViewAll: () => void;
  onSelectStandard: (id: string) => void;
  onAddStandard: () => void;
  onEditStandard: (standard: EvaluationStandardDraft) => void;
  onDeleteStandard: (standardId: string) => void;
  onOpenBulkEdit: () => void;
}> = ({
  standards,
  selectedStandardId,
  filterQuery,
  readOnly = false,
  allowAddStandard = true,
  allowBulkEdit = true,
  editableStandardIds,
  standardScopeById,
  onFilterQueryChange,
  onViewAll,
  onSelectStandard,
  onAddStandard,
  onEditStandard,
  onDeleteStandard,
  onOpenBulkEdit,
}) => {
  const canEditStandard = (standardId: string) => {
    if (readOnly) return false;
    if (editableStandardIds) return editableStandardIds.has(standardId);
    return true;
  };
  const showAddButton = !readOnly && allowAddStandard;
  const showBulkEditButton = !readOnly && allowBulkEdit;

  const filteredStandards = useMemo(() => {
    const list = standards.filter((row) => standardMatchesFilter(row, filterQuery));
    return [...list].sort(compareStandardsByCode);
  }, [standards, filterQuery]);

  return (
    <article
      className="cp-dashboard-block cp-evaluation-plan-standards-block"
      aria-labelledby="cp-evaluation-plan-standards-head"
      data-tutorial-ep="standards-block"
    >
      <h3 className="cp-dashboard-block__head cp-evaluation-plan-section-head" id="cp-evaluation-plan-standards-head">
        <span className="cp-evaluation-plan-section-head-title">성취기준 목록</span>
        <div className="cp-evaluation-plan-section-head-actions">
          {showAddButton || showBulkEditButton ? (
            <>
              {showBulkEditButton ? (
                <button
                  type="button"
                  className="cp-evaluation-plan-section-head-btn"
                  data-tutorial-ep="standards-bulk"
                  onClick={onOpenBulkEdit}
                >
                  일괄등록 및 수정
                </button>
              ) : null}
              {showAddButton ? (
                <button
                  type="button"
                  className="cp-evaluation-plan-section-head-add-btn"
                  data-tutorial-ep="add-standard"
                  onClick={onAddStandard}
                  title="성취기준 추가"
                  aria-label="성취기준 추가"
                >
                  +
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </h3>
      <div className="cp-dashboard-block__body cp-evaluation-plan-section-body cp-evaluation-plan-standards-body">
        <div className="cp-evaluation-plan-filter-bar">
          <label className="cp-evaluation-plan-filter-field cp-evaluation-plan-filter-field--search">
            <span className="cp-evaluation-plan-filter-label">검색</span>
            <input
              type="search"
              className="cp-evaluation-plan-filter-input"
              placeholder="성취기준 검색"
              value={filterQuery}
              onChange={(e) => onFilterQueryChange(e.target.value)}
              aria-label="성취기준 필터"
            />
          </label>
          <button
            type="button"
            className="cp-btn-secondary cp-evaluation-plan-filter-view-all-btn"
            onClick={onViewAll}
          >
            전체 보기
          </button>
        </div>
        {filteredStandards.length === 0 ? (
          <p className="cp-dashboard-placeholder" role="status">
            {standards.length === 0 ? '등록된 성취기준이 없습니다.' : '검색 결과가 없습니다.'}
          </p>
        ) : (
          <ul className="cp-admin-notices-edu-list cp-evaluation-plan-section-list" role="listbox" aria-label="성취기준">
            {filteredStandards.map((standard) => {
              const active = selectedStandardId === standard.id;
              const standardScope = standardScopeById?.[standard.id];
              const editable = canEditStandard(standard.id);
              return (
                <li key={standard.id} className="cp-admin-notices-edu-card" role="none">
                  <div className={`cp-admin-notices-edu-card-inner${active ? ' is-active' : ''}`}>
                    {standardScope ? (
                      <span
                        className={`cp-evaluation-plan-plan-scope-stripe cp-evaluation-plan-plan-scope-stripe--${standardScope}`}
                        aria-hidden
                      />
                    ) : null}
                    <button
                      type="button"
                      className="cp-admin-notices-edu-card-main"
                      onClick={() => onSelectStandard(standard.id)}
                      aria-current={active ? 'true' : undefined}
                    >
                      <span className="cp-admin-notices-edu-card-title">{formatStandardSummary(standard)}</span>
                    </button>
                    <div className="cp-admin-notices-edu-card-tools">
                      {editable ? (
                        <>
                          <button
                            type="button"
                            className="cp-admin-notices-icon-btn"
                            title="수정"
                            aria-label="성취기준 수정"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditStandard(standard);
                            }}
                          >
                            <img
                              src="/edit.svg"
                              alt=""
                              className="cp-admin-notices-tool-icon"
                              width={18}
                              height={18}
                              decoding="async"
                            />
                          </button>
                          <EvaluationPlanDeleteHoldButton
                            itemId={standard.id}
                            onRemove={() => onDeleteStandard(standard.id)}
                          />
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </article>
  );
};

export default EvaluationStandardListPanel;
